import React, { useRef, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
} from '@mui/material'
import type { ButtonProps } from '@mui/material/Button'
import type { SxProps, Theme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import {
  kiotButtonBaseSx,
  kiotButtonCancelSx,
  kiotButtonPrimarySx,
} from '../button/kiotButtonStyles'
import { FieldSurfaceThemeProvider } from '../formFields/FieldSurfaceThemeProvider'
import type { ModalHeaderIntent } from './modalHeaderStyles'
import { modalHeaderTitleBackgroundSx, modalHeaderTitlePaddingSx } from './modalHeaderStyles'
import {
  FORM_MODAL_VIEWPORT_MAX_HEIGHT,
  formModalBelowHeaderOverlaySx,
} from '../styles/formModalLayout'

const resolveKiotFooterButtonSx = (variant: ButtonProps['variant'] = 'outlined') => {
  if (variant === 'contained') return kiotButtonPrimarySx
  if (variant === 'outlined') return kiotButtonCancelSx
  return kiotButtonBaseSx
}

export type BaseModalFooterAction = {
  id?: string | number
  label: React.ReactNode
  onClick: () => void
  variant?: ButtonProps['variant']
  color?: ButtonProps['color']
  disabled?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  fullWidth?: boolean
  sx?: SxProps<Theme>
  type?: 'button' | 'submit' | 'reset'
}

export const baseModalDefaultFooterSx: SxProps<Theme> = {
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1,
  rowGap: 0.75,
  px: { xs: 2, sm: 2.5 },
  pt: 1,
  pb: 0.75,
  borderTop: '1px solid',
  borderColor: 'divider',
  bgcolor: 'transparent',
}

/**
 * Padding mặc định `DialogContent` — đặt trước `contentSx` (consumer có thể ghi đè).
 * Dùng `&&` để tăng specificity: MUI mặc định `.MuiDialogTitle-root + .MuiDialogContent-root { padding-top: 0 }`.
 */
export const baseModalDialogContentPaddingSx: SxProps<Theme> = {
  '&&': {
    px: { xs: 2, sm: 2.5 },
    pt: { xs: 2, sm: 2.25 },
    pb: { xs: 2, sm: 2.5 },
  },
}

export type BaseModalProps = {
  open: boolean
  onClose: () => void
  width?: string | number
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  title?: string | React.ReactNode
  children: React.ReactNode
  contentSx?: SxProps<Theme>
  paperSx?: SxProps<Theme>
  titleSx?: SxProps<Theme>
  dialogTitleSx?: SxProps<Theme>
  closeButtonSx?: SxProps<Theme>
  footerActions?: BaseModalFooterAction[]
  footerSx?: SxProps<Theme>
  /** Thêm / Sửa / Xem — màu nền header phân biệt */
  headerIntent?: ModalHeaderIntent
  closeButtonAriaLabel?: string
  /**
   * `true` → dialog canh giữa màn hình (phù hợp confirm/alert ngắn).
   * `false` (mặc định) → overlay neo sát dưới header menu, không che thanh menu.
   */
  centered?: boolean
}

const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  width,
  maxWidth = 'sm',
  title = 'Thêm mới dữ liệu',
  children,
  contentSx,
  paperSx,
  titleSx,
  dialogTitleSx,
  closeButtonSx,
  footerActions,
  footerSx,
  headerIntent = 'default',
  closeButtonAriaLabel = 'Đóng',
  centered = false,
}) => {
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && contentRef.current) {
      contentRef.current.focus()
    }
  }, [open])

  const footerEls =
    footerActions && footerActions.length > 0 ? (
      <DialogActions
        sx={
          [
            baseModalDefaultFooterSx,
            ...(footerSx ? (Array.isArray(footerSx) ? footerSx : [footerSx]) : []),
          ] as SxProps<Theme>
        }
      >
        {footerActions.map((action, index) => {
          const key = action.id ?? index
          return (
            <Button
              key={key}
              type={action.type ?? 'button'}
              variant={action.variant ?? 'outlined'}
              color={action.color ?? 'primary'}
              size="medium"
              startIcon={action.startIcon}
              endIcon={action.endIcon}
              fullWidth={action.fullWidth}
              disabled={Boolean(action.disabled)}
              onClick={action.onClick}
              sx={[
                resolveKiotFooterButtonSx(action.variant),
                ...(action.sx ? (Array.isArray(action.sx) ? action.sx : [action.sx]) : []),
              ]}
            >
              {action.label}
            </Button>
          )
        })}
      </DialogActions>
    ) : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={!!maxWidth}
      scroll="paper"
      sx={centered ? undefined : formModalBelowHeaderOverlaySx}
      slotProps={{
        backdrop: {
          sx: centered ? undefined : formModalBelowHeaderOverlaySx,
        },
        container: {
          sx: {
            alignItems: centered ? 'center' : 'flex-start',
          },
        },
        paper: {
          sx: [
            {
              ...(width != null && width !== '' ? { width } : {}),
              minHeight: 120,
              height: 'auto',
              maxHeight: FORM_MODAL_VIEWPORT_MAX_HEIGHT,
              mt: 0,
              mb: 0,
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '8px',
            },
            ...(paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : []),
          ],
        },
      }}
    >
      <DialogTitle
        sx={[
          {
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          },
          modalHeaderTitlePaddingSx,
          modalHeaderTitleBackgroundSx(headerIntent),
          ...(dialogTitleSx ? (Array.isArray(dialogTitleSx) ? dialogTitleSx : [dialogTitleSx]) : []),
        ]}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, minWidth: 0 }}>
          <Typography
            variant="h6"
            component="span"
            sx={[
              {
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                fontWeight: 600,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
              ...(titleSx ? (Array.isArray(titleSx) ? titleSx : [titleSx]) : []),
            ]}
          >
            {title}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={closeButtonAriaLabel}
            sx={[
              { flexShrink: 0, color: 'text.secondary' },
              ...(closeButtonSx ? (Array.isArray(closeButtonSx) ? closeButtonSx : [closeButtonSx]) : []),
            ]}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        ref={contentRef}
        tabIndex={-1}
        sx={[
          {
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          baseModalDialogContentPaddingSx,
          ...(contentSx ? (Array.isArray(contentSx) ? contentSx : [contentSx]) : []),
        ]}
      >
        <FieldSurfaceThemeProvider surface="form">{children}</FieldSurfaceThemeProvider>
      </DialogContent>
      {footerEls}
    </Dialog>
  )
}

export default BaseModal
